import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { couponApi, userApi } from '@/services/api'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { Plus, Edit2, Trash2, Eye, EyeOff, Users, Info } from 'lucide-react'
import { Coupon } from '@/types'

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage Discount' },
  { value: 'fixed_amount', label: 'Fixed Amount Discount' },
]

export default function Coupons() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [showUsageModal, setShowUsageModal] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [viewingCoupon, setViewingCoupon] = useState<any>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage' as 'percentage' | 'fixed_amount',
    discount_value: '',
    recurring_cycles: '0',
    start_date: '',
    expiry_date: '',
    assigned_user_id: '',
    max_uses: '0',
    is_active: true,
  })

  const { data } = useQuery({
    queryKey: ['coupons'],
    queryFn: () => couponApi.list(),
  })

  const { data: usageData } = useQuery({
    queryKey: ['coupon-usage', viewingCoupon?.id],
    queryFn: () => couponApi.get(viewingCoupon.id),
    enabled: !!viewingCoupon,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => couponApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] })
      setShowModal(false)
      setEditingCoupon(null)
      resetForm()
      setSuccess('Coupon created successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create coupon')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      couponApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] })
      setShowModal(false)
      setEditingCoupon(null)
      resetForm()
      setSuccess('Coupon updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update coupon')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => couponApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] })
      setSuccess('Coupon deleted successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete coupon')
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      couponApi.update(id, { is_active: is_active ? 1 : 0 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to toggle coupon')
    },
  })

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      discount_value: '',
      recurring_cycles: '0',
      start_date: '',
      expiry_date: '',
      assigned_user_id: '',
      max_uses: '0',
      is_active: true,
    })
    setError('')
    setSearchResults([])
    setSearchQuery('')
  }

  const handleCreate = () => {
    setEditingCoupon(null)
    resetForm()
    setShowModal(true)
  }

  const handleEdit = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setFormData({
      code: coupon.code,
      type: coupon.type,
      discount_value: coupon.discount_value.toString(),
      recurring_cycles: coupon.recurring_cycles.toString(),
      start_date: coupon.start_date ? coupon.start_date.split(' ')[0] : '',
      expiry_date: coupon.expiry_date ? coupon.expiry_date.split(' ')[0] : '',
      assigned_user_id: coupon.assigned_user_id?.toString() || '',
      max_uses: coupon.max_uses.toString(),
      is_active: coupon.is_active === 1,
    })

    // If user is assigned, set search results
    if (coupon.assigned_email) {
      setSearchResults([{
        id: coupon.assigned_user_id,
        first_name: coupon.assigned_first_name,
        last_name: coupon.assigned_last_name,
        email: coupon.assigned_email,
      }])
      setSearchQuery(`${coupon.assigned_first_name} ${coupon.assigned_last_name} (${coupon.assigned_email})`)
    }

    setShowModal(true)
  }

  const handleViewUsage = (coupon: Coupon) => {
    setViewingCoupon(coupon)
    setShowUsageModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.code.trim()) {
      setError('Please enter a coupon code')
      return
    }

    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      setError('Please enter a valid discount value')
      return
    }

    if (formData.type === 'percentage' && parseFloat(formData.discount_value) > 100) {
      setError('Percentage discount cannot exceed 100%')
      return
    }

    const submitData = {
      code: formData.code.toUpperCase().trim(),
      type: formData.type,
      discount_value: parseFloat(formData.discount_value),
      recurring_cycles: parseInt(formData.recurring_cycles) || 0,
      start_date: formData.start_date || null,
      expiry_date: formData.expiry_date || null,
      assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
      max_uses: parseInt(formData.max_uses) || 0,
      is_active: formData.is_active ? 1 : 0,
    }

    if (editingCoupon) {
      updateMutation.mutate({ id: editingCoupon.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this coupon?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleActive = (id: number, currentStatus: number) => {
    toggleActiveMutation.mutate({ id, is_active: currentStatus !== 1 })
  }

  const handleUserSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.length >= 2) {
      try {
        const response = await userApi.search(query)
        setSearchResults(response.data?.users || [])
      } catch (err) {
        console.error('Failed to search users:', err)
        setSearchResults([])
      }
    } else {
      setSearchResults([])
    }
  }

  const handleSelectUser = (user: any) => {
    setFormData({ ...formData, assigned_user_id: user.id.toString() })
    setSearchQuery(`${user.first_name} ${user.last_name} (${user.email})`)
    setSearchResults([])
  }

  const handleClearUser = () => {
    setFormData({ ...formData, assigned_user_id: '' })
    setSearchQuery('')
    setSearchResults([])
  }

  const coupons = data?.data?.coupons || []
  const usage = usageData?.data?.usage || []

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.type === 'percentage') {
      return `${parseFloat(String(coupon.discount_value))}%`
    }
    return `$${parseFloat(String(coupon.discount_value)).toFixed(2)}`
  }

  const isExpired = (coupon: Coupon) => {
    if (!coupon.expiry_date) return false
    return new Date(coupon.expiry_date) < new Date()
  }

  const isNotStarted = (coupon: Coupon) => {
    if (!coupon.start_date) return false
    return new Date(coupon.start_date) > new Date()
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Coupon Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Create and manage subscription coupons and promotions
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Coupon
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && !showModal && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Coupons Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ResponsiveTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="2">
                  Cycles
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="1">
                  Validity
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="3">
                  Usage
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="4">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No coupons yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                coupons.map((coupon: Coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-mono font-bold text-gray-900">
                          {coupon.code}
                        </span>
                        {coupon.assigned_email && (
                          <span className="text-xs text-gray-500">
                            Assigned to: {coupon.assigned_email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {formatDiscount(coupon)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {coupon.type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                        </span>
                      </div>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-priority="2" data-label="Cycles">
                      {coupon.recurring_cycles === 0 ? 'Unlimited' : coupon.recurring_cycles}
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="1" data-label="Validity">
                      <div className="flex flex-col text-xs text-gray-600">
                        <span>Start: {formatDate(coupon.start_date)}</span>
                        <span>End: {formatDate(coupon.expiry_date)}</span>
                      </div>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="3" data-label="Usage">
                      <button
                        onClick={() => handleViewUsage(coupon)}
                        className="text-sm text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        {coupon.current_uses}
                        {coupon.max_uses > 0 && ` / ${coupon.max_uses}`}
                      </button>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="4" data-label="Status">
                      <div className="flex flex-col space-y-1">
                        <Badge
                          status={coupon.is_active && !isExpired(coupon) && !isNotStarted(coupon) ? 'active' : 'inactive'}
                        >
                          {coupon.is_active ? (isExpired(coupon) ? 'Expired' : isNotStarted(coupon) ? 'Not Started' : 'Active') : 'Inactive'}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        className="mobile-details-trigger text-blue-600 hover:text-blue-900"
                        title="View Hidden Details"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(coupon.id, coupon.is_active)}
                        className="text-gray-600 hover:text-gray-900"
                        title={coupon.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {coupon.is_active ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(coupon)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ResponsiveTable>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal
          title={editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
          onClose={() => {
            setShowModal(false)
            setEditingCoupon(null)
            resetForm()
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert type="error" message={error} onClose={() => setError('')} />}

            <div>
              <label className="label">Coupon Code *</label>
              <input
                type="text"
                className="input uppercase"
                placeholder="e.g., SAVE20, WELCOME2024"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                pattern="[A-Z0-9-]+"
                title="Only uppercase letters, numbers, and dashes allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use uppercase letters, numbers, and dashes only
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Discount Type *</label>
                <select
                  className="select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'percentage' | 'fixed_amount' })}
                  required
                >
                  {DISCOUNT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Discount Value *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={formData.type === 'percentage' ? '100' : undefined}
                  className="input"
                  placeholder={formData.type === 'percentage' ? '0-100' : '0.00'}
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Recurring Billing Cycles</label>
                <input
                  type="number"
                  min="0"
                  className="input"
                  placeholder="0 for unlimited"
                  value={formData.recurring_cycles}
                  onChange={(e) => setFormData({ ...formData, recurring_cycles: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  0 = unlimited cycles
                </p>
              </div>

              <div>
                <label className="label">Max Uses</label>
                <input
                  type="number"
                  min="0"
                  className="input"
                  placeholder="0 for unlimited"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  disabled={!!formData.assigned_user_id}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.assigned_user_id ? 'N/A (user assigned)' : '0 = unlimited users'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank for immediate start
                </p>
              </div>

              <div>
                <label className="label">Expiry Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank for no expiry
                </p>
              </div>
            </div>

            <div>
              <label className="label">Assign to Specific User (Optional)</label>
              <div className="relative">
                <input
                  type="text"
                  className="input"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => handleUserSearch(e.target.value)}
                />
                {formData.assigned_user_id && (
                  <button
                    type="button"
                    onClick={handleClearUser}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
                {searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 border-b border-gray-200"
                        onClick={() => handleSelectUser(user)}
                      >
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Leave blank for any user to use this coupon
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active (available for use)
              </label>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowModal(false)
                  setEditingCoupon(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {editingCoupon ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Usage Modal */}
      {showUsageModal && viewingCoupon && (
        <Modal
          title={`Coupon Usage: ${viewingCoupon.code}`}
          onClose={() => {
            setShowUsageModal(false)
            setViewingCoupon(null)
          }}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-xs text-gray-500">Total Uses</p>
                <p className="text-2xl font-bold">{viewingCoupon.current_uses}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Max Uses</p>
                <p className="text-2xl font-bold">
                  {viewingCoupon.max_uses === 0 ? 'Unlimited' : viewingCoupon.max_uses}
                </p>
              </div>
            </div>

            {usage.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No usage data yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Plan
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Cycles Remaining
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Applied At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usage.map((u: any) => (
                      <tr key={u.id}>
                        <td className="px-4 py-2 text-sm">
                          <div>
                            <p className="font-medium text-gray-900">
                              {u.first_name} {u.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {u.plan_type || 'N/A'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {u.cycles_remaining === null ? 'Unlimited' : u.cycles_remaining}
                        </td>
                        <td className="px-4 py-2">
                          <Badge status={u.status === 'active' ? 'active' : 'inactive'}>
                            {u.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">
                          {formatDate(u.first_applied_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}
